import * as React from "react"

import { cva, type VariantProps } from "class-variance-authority"
import { CircleHelp, Info } from "lucide-react"

import { cn } from "../../cn"
import { Tooltip } from "../Tooltip/Tooltip"

const labelVariants = cva([
  "text-base font-semibold text-coval-text text-sm",
  "peer-disabled:opacity-50",
])

export interface LabelProps
  extends
    React.LabelHTMLAttributes<HTMLLabelElement>,
    VariantProps<typeof labelVariants> {
  tooltip?: React.ReactNode
  /** Icon that opens the tooltip: "info" (an i, the default) or "help" (a question mark). */
  tooltipIcon?: "help" | "info"
  description?: React.ReactNode
  descriptionId?: string
}

function LabelTooltip({
  tooltip,
  icon,
}: {
  tooltip: React.ReactNode
  icon: "help" | "info"
}) {
  const TooltipIcon = icon === "info" ? Info : CircleHelp
  return (
    <Tooltip content={tooltip}>
      <button
        type="button"
        onClick={(e) => e.preventDefault()}
        aria-label="Help"
      >
        <TooltipIcon className="size-3.5 text-coval-text-muted" />
      </button>
    </Tooltip>
  )
}

const Label = React.forwardRef<HTMLLabelElement, LabelProps>(
  (
    {
      className,
      children,
      tooltip,
      tooltipIcon = "info",
      description,
      descriptionId: descriptionIdProp,
      ...props
    },
    ref,
  ) => {
    const generatedId = React.useId()
    const descriptionId = descriptionIdProp ?? generatedId

    return (
      <label
        className={cn(
          labelVariants(),
          description
            ? "inline-flex flex-col items-start gap-1"
            : "inline-flex items-center gap-1",
          className,
        )}
        ref={ref}
        {...props}
      >
        <span className="inline-flex items-center gap-1">
          {children}
          {tooltip && <LabelTooltip tooltip={tooltip} icon={tooltipIcon} />}
        </span>
        {description && (
          <span id={descriptionId} className="font-normal">
            {description}
            {tooltip && <span className="sr-only"> {tooltip}</span>}
          </span>
        )}
      </label>
    )
  },
)

Label.displayName = "Label"

export interface GroupLabelProps extends React.HTMLAttributes<HTMLSpanElement> {
  tooltip?: React.ReactNode
  /** Icon that opens the tooltip: "info" (an i, the default) or "help" (a question mark). */
  tooltipIcon?: "help" | "info"
}

/**
 * Label text for something a `<label>` cannot name: a group of controls, a
 * tab list, or a code editor. It looks like `Label` and carries the same help
 * tooltip. The `id` lands on the text alone, so a control that points
 * `aria-labelledby` at it is named without the help button.
 */
const GroupLabel = React.forwardRef<HTMLSpanElement, GroupLabelProps>(
  (
    { className, children, id, tooltip, tooltipIcon = "info", ...props },
    ref,
  ) => (
    <span
      className={cn(
        labelVariants(),
        "inline-flex items-center gap-1",
        className,
      )}
      ref={ref}
      {...props}
    >
      <span id={id}>{children}</span>
      {tooltip && <LabelTooltip tooltip={tooltip} icon={tooltipIcon} />}
    </span>
  ),
)

GroupLabel.displayName = "GroupLabel"

export { GroupLabel, Label, labelVariants }
